// components/GdprBanner.jsx
import { h, Component } from 'preact';

import { isNativeApp } from '../../lib/nativeApp';

const CONSENT_STORAGE_KEY = 'gdprConsent.v1';   // bump to re-prompt after text/logic changes

// The native store apps (mobileApp wrappers, ?platform=ios|android or the cordova bridge)
// never show the cookie banner and never get analytics/marketing consent: App Review reads a
// cookie prompt as tracking without App Tracking Transparency (guideline 5.1.2(i), rejection
// 2026-09-06), and the App Privacy answers declare no analytics. Without consent app.js keeps
// skipping Firebase Auth + Analytics. The web site keeps its banner.
export function isConsentBannerSuppressed(win) {
    return isNativeApp(win);
}

export function loadConsent() {
    try { return JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || '{}'); }
    catch (_) { return {}; }
}

export function getConsent() {
    try { return JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || '{}'); }
    catch (_) { return {}; }
}

function saveConsent(consent) {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({ ...consent, ts: Date.now() }));
    window.dispatchEvent(new CustomEvent('gdprConsentChanged', { detail: consent }));
}

export function hasConsent(category) {
    const c = loadConsent();
    if (!c || !c.given) return false;
    if (category === 'necessary') return true; // always allowed
    if (isConsentBannerSuppressed()) return false; // native store apps: no analytics/marketing
    return !!c[category];
}

export function onConsentChange(cb) {
    const handler = (e) => cb(e.detail || loadConsent());
    window.addEventListener('gdprConsentChanged', handler);
    return () => window.removeEventListener('gdprConsentChanged', handler);
}


// NEW: update from anywhere (profile page)
export function setConsent(partial) {
    const current = getConsent();
    const next = { given: true, analytics: !!current.analytics, marketing: !!current.marketing, ...partial };
    saveConsent(next);
    return next;
}

// NEW: clear (and optionally reopen the banner)
export function clearConsent({ reopen = false } = {}) {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    const detail = { given: false };
    window.dispatchEvent(new CustomEvent('gdprConsentChanged', { detail }));
    if (reopen) {
        window.dispatchEvent(new CustomEvent('gdprOpen', { detail: { force: true } }));
    }
}

// OPTIONAL: open banner UI from anywhere
export function openConsentDialog(initial = {}) {
    window.dispatchEvent(new CustomEvent('gdprOpen', { detail: initial }));
}

export default class GdprBanner extends Component {
    constructor(props) {
        super(props);
        const existing = loadConsent();
        this.state = {
            show: !existing.given && !isConsentBannerSuppressed(),
            showMore: false,
            analytics: !!existing.analytics,
            marketing: !!existing.marketing,
            locale: props.locale || 'en'
        };
    }

    texts() {
        const t = {
            en: {
                title: 'Privacy choices',
                body: 'We use cookies and similar tech to make the site work and, with your consent, to measure traffic and improve the service.',
                policy: 'Privacy policy',
                acceptAll: 'Accept all',
                necessaryOnly: 'Only necessary',
                customise: 'Settings',
                save: 'Save choices',
                analytics: 'Analytics',
                marketing: 'Marketing',
                on: 'On',
                off: 'Off'
            },
            nb: {
                title: 'Dine personvernvalg',
                body: 'Vi bruker informasjonskapsler og lignende for at siden skal fungere og, med ditt samtykke, for å måle trafikk og forbedre tjenesten.',
                policy: 'Personvernerklæring',
                acceptAll: 'Godta alle',
                necessaryOnly: 'Kun nødvendige',
                customise: 'Innstillinger',
                save: 'Lagre valg',
                analytics: 'Analyse',
                marketing: 'Markedsføring',
                on: 'På',
                off: 'Av'
            }
        };
        return t[this.state.locale] || t.en;
    }

    componentDidMount() {
        window.addEventListener('gdprOpen', this.onOpen);
    }

    componentWillUnmount() {
        window.removeEventListener('gdprOpen', this.onOpen);
    }

    onOpen = (e) => {
        if (isConsentBannerSuppressed()) return;
        const existing = getConsent();
        const init = e && e.detail ? e.detail : {};
        this.setState({
            show: true,
            // showMore: true,
            analytics: 'analytics' in init ? !!init.analytics : !!existing.analytics,
            marketing: 'marketing' in init ? !!init.marketing : !!existing.marketing
        });
    }

    acceptAll = () => {
        const consent = { given: true, analytics: true, marketing: true };
        saveConsent(consent);
        this.setState({ show: false, ...consent });
        this.props.onAfterConsent && this.props.onAfterConsent(consent);
    }

    necessaryOnly = () => {
        const consent = { given: true, analytics: false, marketing: false };
        saveConsent(consent);
        this.setState({ show: false, ...consent });
        this.props.onAfterConsent && this.props.onAfterConsent(consent);
    }

    saveCustom = () => {
        const { analytics, marketing } = this.state;
        const consent = { given: true, analytics, marketing };
        saveConsent(consent);
        this.setState({ show: false });
        this.props.onAfterConsent && this.props.onAfterConsent(consent);
    }

    render(_, { show, showMore, analytics, marketing }) {
        if (!show) return null;
        const t = this.texts();
        return (
            <div
                role='dialog'
                aria-live='polite'
                class='position-fixed w-100'
                style='left:0; right:0; bottom:0; z-index:2147483000;'
            >
                <div class='container'>
                    <div
                        class='d-flex flex-column flex-md-row align-items-start align-items-md-center p-3 mb-2 rounded-lg shadow'
                        style={`
                            background-color: rgba(0, 0, 0, 0.7);
                            color:#fff;
                        `}
                    >
                        <div class='flex-fill mr-md-3'>
                            <strong>{t.title}</strong>
                            <div class='small mt-1'>
                                {t.body}
                                {/* <a href='/privacy' class='text-light' target='_blank' rel='noopener'>{t.policy}</a>. */}
                            </div>
                            {showMore && (
                                <div class='mt-2'>
                                    <label class='me-3'>
                                        {t.analytics}{' '}
                                        <input type='checkbox' checked={analytics} onChange={e => this.setState({ analytics: e.currentTarget.checked })} />
                                    </label>
                                    <label class='me-3'>
                                        {t.marketing}{' '}
                                        <input type='checkbox' checked={marketing} onChange={e => this.setState({ marketing: e.currentTarget.checked })} />
                                    </label>
                                </div>
                            )}
                        </div>

                        <div class='d-flex align-items-center mt-2 mt-md-0'>
                            <button type='button' class='btn btn-sm btn-light rounded-pill me-2' onClick={this.necessaryOnly}>
                                <nobr>{t.necessaryOnly}</nobr>
                            </button>
                            <button type='button' class='btn btn-sm btn-primary rounded-pill me-2' onClick={this.acceptAll}>
                                <nobr>{t.acceptAll}</nobr>
                            </button>
                            {/* <button type='button' class='btn btn-sm btn-outline-light' onClick={() => this.setState({ showMore: !showMore })}>
                                <nobr>{showMore ? t.save : t.customise}</nobr>
                            </button> */}
                            {showMore && (
                                <button type='button' class='btn btn-sm btn-success ms-2' onClick={this.saveCustom}>
                                    <nobr>{t.save}</nobr>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
